import React, { useContext, useState, useRef, useEffect } from "react";
import { ProfileContext } from "../contexts/ProfileContext";
import styled from "styled-components";
import "../styles/Dashboard.css";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  LogarithmicScale,
} from "chart.js";
import { Bar, Doughnut, Line, Pie } from "react-chartjs-2";
import { LoadingSpinner } from "../components/loading-spinner";
import { useReactToPrint } from "react-to-print";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardStats } from "./dashboard-stats";
import BarchartAreacode from "./Barchart-areacode";
import { OrganizationContext } from "../contexts/OrganizationContext";
import StackbarcartWorkhours from "./Stackbarcart-workhours";

ChartJS.register(
  ArcElement,
  Tooltip,
  Title,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LogarithmicScale
);

//the dashboard component that illustrates raw data into pie and bar charts
export const Dashboard = () => {
  const [exportOption, setExportOption] = useState("");
  const [isAreaCodeChartVisible, setIsAreaCodeChartVisible] = useState(false);
  const [isAgentWorkChartVisible, setIsAgentWorkChartVisible] = useState(false);
  // State for work hours custom date range
  const [workHoursDateRange, setWorkHoursDateRange] = useState(null);

  // UPDATED: Enhanced state for work hours details query parameters
  const [workHoursDetailsParams, setWorkHoursDetailsParams] = useState({
    startDate: null,
    endDate: null,
    department: null,
    enabled: false,
    clickedDate: null,
    modalDateRange: null, // Track the modal's current date range
  });

  const componentToPrint = useRef();

  const {
    setShowModel,
    showModel,
    organization,
    setShowReports,
    dateRange,
    generateReportsCharts,
    evenlyDistribute,
    worker,
    getOrganizationsReport,
    organizationNumber,
    getAreaCodeStats,
    subscriberAreaCodeChartAllowed,

    getAgentWorkHours,
    getAgentWorkHoursDetails,
    getCurrentOrganization,
  } = useContext(ProfileContext);

  const { organizationNumbers, nameClicked, filterByArray, setNameClicked } =
    useContext(OrganizationContext);
  const queryClient = useQueryClient();

  const organizationName = worker?.organizationName;

  const { isPending, status, isError, data, error } = useQuery({
    queryKey: [
      "report",
      {
        organizationNumber,
        showModel,
        dateRange,
        organizationId: organization?.id,
        organizationName: organization?.name,
      },
    ],
    queryFn: getOrganizationsReport,
    enabled: !!organizationName,
  });

  // ✅ Area Code Query (Updated)
  const {
    data: areaCodeData,
    isPending: isAreaCodePending,
    isError: isAreaCodeError,
  } = useQuery({
    queryKey: [
      "areaCodeStats",
      {
        organizationNumber,
        dateRange,
        organizationId: organization?.id,
        organizationName: organization?.name,
      },
    ],
    queryFn: async ({ queryKey }) => {
      const [, { organizationNumber, dateRange }] = queryKey;

      const orgNumber =
        organizationNumber || process.env.REACT_APP_TWILIO_NUMBER;

      let startDate = null;
      let endDate = null;

      // ✅ Handle date logic (like AreaCodesCalls)
      if (dateRange && dateRange.length === 2) {
        startDate = dateRange[0].toISOString().split("T")[0];
        endDate = dateRange[1].toISOString().split("T")[0];
      } else {
        // Default: ALL DATA
        startDate = "2015-01-01";
        endDate = null;
      }

      const response = await getAreaCodeStats(orgNumber, startDate, endDate);

      console.log("✅ Area Code API Response (Dashboard):", response);

      // ✅ Adapt response format like in AreaCodesCalls
      if (response?.labels && response?.values) {
        const formattedData = response.labels.reduce((acc, label, i) => {
          acc[label] = response.values[i];
          return acc;
        }, {});
        return formattedData;
      } else if (response?.original?.data || response?.data) {
        return response?.original?.data || response?.data;
      } else {
        return null;
      }
    },
    enabled: !!organizationName,
  });

  // Helper function to calculate date range
  const getDateRange = (customDateRange = null) => {
    // Priority 1: Custom work hours date range from filter
    if (customDateRange) {
      return customDateRange;
    }

    const today = new Date();
    let startDate, endDate;

    // Priority 2: Global date range from date picker
    if (dateRange && dateRange.length === 2) {
      return {
        startDate: dateRange[0].toISOString().split("T")[0],
        endDate: dateRange[1].toISOString().split("T")[0],
      };
    }

    // Priority 3: Based on showModel
    if (showModel === "monthly") {
      // Current month
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = today;
    } else {
      // Default to last 7 days for "all" data
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 6);
      endDate = today;
    }

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // 3036 - Twilio Report start here - Uncomment bellow code to call APIs

  //// Work hours query with custom date range support
  const {
    data: workHoursData,
    isPending: isWorkHoursPending,
    isError: isWorkHoursError,
    refetch: refetchWorkHours,
  } = useQuery({
    queryKey: [
      "workHours",
      { organizationNumber, showModel, dateRange, workHoursDateRange },
    ],
    queryFn: ({ queryKey }) => {
      const [
        ,
        { organizationNumber, showModel, dateRange, workHoursDateRange },
      ] = queryKey;

      const { startDate, endDate } = getDateRange(workHoursDateRange);
      return getAgentWorkHours(startDate, endDate);
    },
    enabled: !!organizationName,
  });

  // UPDATED: Enhanced work hours details query - handles modal date range
  const {
    data: workHoursDetailsData,
    isPending: isWorkHoursDetailsPending,
    isError: isWorkHoursDetailsError,
    refetch: refetchWorkHoursDetails,
  } = useQuery({
    queryKey: [
      "workHoursDetails",
      {
        organizationNumber,
        startDate: workHoursDetailsParams.startDate,
        endDate: workHoursDetailsParams.endDate,
        department: workHoursDetailsParams.department,
        clickedDate: workHoursDetailsParams.clickedDate,
        modalDateRange: workHoursDetailsParams.modalDateRange,
      },
    ],
    queryFn: ({ queryKey }) => {
      const [, { startDate, endDate, department }] = queryKey;

      console.log("Fetching work hours details with:", {
        startDate,
        endDate,
        department,
      });

      // Use the modal's current date range
      return getAgentWorkHoursDetails(startDate, endDate, department);
    },
    enabled: workHoursDetailsParams.enabled && !!organizationName,
  });

  // 3036 - Twilio report End here

  useEffect(() => {
    setShowReports(true);

    if (worker && worker.role === "super_admin") {
      setIsAreaCodeChartVisible(true);
      setIsAgentWorkChartVisible(true);
    }
  }, []);

  useEffect(() => {
    console.log("Demo check component update ------- ");
    if (subscriberAreaCodeChartAllowed) {
      setIsAreaCodeChartVisible(true);
    }
    if (worker && worker.role === "super_admin") {
      setIsAreaCodeChartVisible(true);
      setIsAgentWorkChartVisible(true);
    }
  }, [subscriberAreaCodeChartAllowed]);

  // Log the work hours details data
  // 3036 - Twilio Report  start here- use effect uncomment below code
  useEffect(() => {
    console.log("Work Hours Details Data:", workHoursDetailsData);
    console.log("Work Hours Details Params:", workHoursDetailsParams);
  }, [workHoursDetailsData, workHoursDetailsParams]);

  // 3036 - Twilio Report  end here

  // Invalidate queries when organization changes to force re-fetch
  useEffect(() => {
    if (organization?.id) {
      queryClient.invalidateQueries({ queryKey: ["report"] });
      queryClient.invalidateQueries({ queryKey: ["areaCodeStats"] });
    }
  }, [organization?.id, organization?.name, queryClient]);

  const handleSwitch = (e) => {
    setShowModel(e.target.value);
    // Reset work hours custom date range when switching models
    setWorkHoursDateRange(null);
    // Reset details params when switching models
    setWorkHoursDetailsParams({
      startDate: null,
      endDate: null,
      department: null,
      enabled: false,
      clickedDate: null,
      modalDateRange: null,
    });
  };

  // Handler for work hours filter changes
  const handleWorkHoursFilterChange = (newDateRange) => {
    console.log("Work hours filter changed:", newDateRange);
    setWorkHoursDateRange(newDateRange);
  };

  // UPDATED: Handler for when a bar is clicked in work hours chart
  const handleWorkHoursBarClick = (type, clickedDate, modalDateRange) => {
    console.log("Bar clicked:", { type, clickedDate, modalDateRange });

    // Update the details params with the modal's date range
    // This supports both single day view and expanded date ranges from modal filters
    setWorkHoursDetailsParams({
      startDate: modalDateRange.startDate,
      endDate: modalDateRange.endDate,
      department: type, // 'staff' or 'volunteer'
      enabled: true,
      clickedDate: clickedDate, // Keep track of originally clicked date
      modalDateRange: modalDateRange, // Track modal's current date range
    });
  };

  const handleExportOption = (e) => {
    setExportOption(e.target.value);

    if (e.target.value === "print") {
      handlePrint();
    } else if (e.target.value === "export") {
      handleExport();
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentToPrint.current,
    documentTitle: "Anyone Pray Dashboard",
    onAfterPrint: () => console.log("Printed PDF successfully!"),
  });

  const getLabelCounts = (data, labelKey) => {
    data = data && data.toString().split(",");
    const elementCounts = {};
    data.forEach((element) => {
      element = element.trim();
      elementCounts[element] = (elementCounts[element] || 0) + 1;
    });

    return Object.keys(elementCounts).map((label) => ({
      [labelKey]: label,
      total: elementCounts[label],
    }));
  };

  if (isPending) return <LoadingSpinner />;
  if (isError) return <p>{JSON.stringify(error)}</p>;

  const [
    dispositionData,
    callTypeData,
    callTypeBreakdownData,
    ageRangeData,
    genderData,
    digitalResourcesData,
    physicalResourcesData,
  ] = generateReportsCharts(data?.details, data?.stats?.total_calls);

  const handleExport = () => {
    // Combine data arrays if necessary
    const csvData1 = Papa.unparse(
      getLabelCounts(data.details.call_type_report, "call_types"),
      { header: true }
    );
    const csvData2 = Papa.unparse(
      evenlyDistribute(data.details.disposition_report, data.stats.total_calls),
      { header: true }
    );
    const csvData3 = Papa.unparse(data.details.gender_report, { header: true });
    const csvData4 = Papa.unparse(data.details.age_range_report, {
      header: true,
    });
    const csvData5 = Papa.unparse(
      getLabelCounts(data.details.digital_resource_report, "digital_resources"),
      { header: true }
    );

    // Combine the two CSV strings
    const combinedCSV = `${csvData1}\n\n${csvData2}\n\n${csvData3}\n\n${csvData4}\n\n${csvData5}\n\n`;

    // Create a Blob from the combined CSV string
    const blob = new Blob([combinedCSV], { type: "text/csv;charset=utf-8;" });

    // Use file-saver to trigger the download
    saveAs(blob, "AnyonePrayDashboard.csv");
  };

  return (
    <Wrapper>
      <ButtonWrapper>
        <ToggleButtonWrapper className={dateRange ? " disabled m-0" : "m-0"}>
          <div className="container">
            <div className="switches-container">
              <input
                type="radio"
                id="switchAll"
                name="switchData"
                value="all"
                checked={showModel === "all"}
                onChange={handleSwitch}
              />
              <input
                type="radio"
                id="switchMonthly"
                name="switchData"
                value="monthly"
                checked={showModel === "monthly"}
                onChange={handleSwitch}
              />
              <label htmlFor="switchAll">ALL DATA</label>
              <label htmlFor="switchMonthly">CURRENT MONTH</label>
              <div className="switch-wrapper">
                <div className="switch">
                  <div>ALL DATA</div>
                  <div>CURRENT MONTH</div>
                </div>
              </div>
            </div>
          </div>
        </ToggleButtonWrapper>

        <ExportOption
          onChange={(e) => {
            handleExportOption(e);
          }}
          className="rounded mr-2 bg-[#FAF8F8] hover:bg-[#ADCFD4] border-1 border-[#106271]  text-[#106271]"
          value={exportOption}
        >
          <option value="">Export Options</option>
          <option value="print"> Print PDF</option>
          <option value="export">Export as Excel Sheet</option>
        </ExportOption>
      </ButtonWrapper>

      <ChartsWrapper className="p-3" ref={componentToPrint}>
        {organizationName === "Crossroads" && <DashboardStats />}
        <DarkCard className="col-md-6 col-sm-10 mb-5 p-2">
          <DarkCardValue>
            {data.stats.total_calls.toLocaleString("en-US")}
          </DarkCardValue>
          <DarkCardLabel>total calls received</DarkCardLabel>
        </DarkCard>

        <Charts className="row m-3">
          <div className="col-md-10 col-sm-12">
            <Bar
              className="col"
              data={callTypeData}
              options={{
                scales: {
                  y: {
                    type: "logarithmic",
                    ticks: {
                      callback: function (value) {
                        return value.toLocaleString();
                      },
                    },
                  },
                },
              }}
            />
          </div>
          {organization && organization.prayerPartnersCount && (
            <CardContainer className="col-md-4 col-sm-10">
              <LightCard className="p-5">
                <LightCardValue>
                  {organization.prayerPartnersCount?.toLocaleString("en-US")}
                </LightCardValue>
                <LightCardLabel># of volunteers onboarded</LightCardLabel>
              </LightCard>
            </CardContainer>
          )}
        </Charts>

        <Charts className="row mb-5">
          {callTypeBreakdownData.map((breakdown, index) => {
            return (
              <div className="col-md-6" key={index}>
                <Bar
                  data={breakdown}
                  options={{
                    maintainAspectRatio: true,
                    plugins: {
                      title: {
                        display: true,
                        position: "top",
                        font: { size: 16 },
                      },
                      legend: { labels: { font: { size: 14 } } },
                    },
                    scales: {
                      y: {
                        type: "logarithmic",
                        ticks: {
                          callback: function (value) {
                            return value.toLocaleString();
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            );
          })}
        </Charts>

        <Charts className="row">
          <div className="col-md-6">
            <Doughnut
              data={dispositionData}
              options={{
                maintainAspectRatio: true,
                plugins: {
                  title: {
                    display: true,
                    text: "Disposition of Calls",
                    position: "top",
                    font: { size: 16 },
                  },
                  legend: { position: "right", labels: { font: { size: 14 } } },
                },
              }}
            />
          </div>
          <div className="col-md-6">
            <Doughnut
              data={genderData}
              options={{
                plugins: {
                  title: {
                    display: true,
                    text: "Gender of Callers",
                    position: "top",
                    font: { size: 16 },
                  },
                  legend: { position: "right", labels: { font: { size: 14 } } },
                },
              }}
            />
          </div>
        </Charts>

        <Charts className="row">
          <div className="col-md-6 col-sm-12">
            <Line className="col" data={ageRangeData} />
          </div>
          <CardContainer className="col-md-4 col-sm-10">
            <LightCard className="p-4">
              <LightCardValue>
                {Array.isArray(data.stats.total_time_in_secs)
                  ? Math.floor(
                      parseInt(data.stats.total_time_in_secs) / 3600
                    ).toLocaleString("en-US")
                  : Math.floor(parseInt(data.stats.total_time_in_secs) / 3600) >
                    0
                  ? Math.floor(
                      parseInt(data.stats.total_time_in_secs) / 3600
                    ).toLocaleString("en-US") + "hrs"
                  : "< 1 hr"}
              </LightCardValue>
              <LightCardLabel>total call duration</LightCardLabel>
            </LightCard>
          </CardContainer>
        </Charts>

        <Charts className="row">
          <div className="col-md-6 col-sm-12 ">
            <Doughnut
              data={digitalResourcesData}
              options={{
                plugins: {
                  title: {
                    display: true,
                    text: "Digital Resource Reports",
                    position: "top",
                    font: { size: 16 },
                  },
                  legend: { position: "right", labels: { font: { size: 10 } } },
                },
              }}
            />
          </div>
          {/* Check if there are physical resources sent| subscribers cant see physical resources */}
          {physicalResourcesData.labels[0] !== "" &&
            worker.role !== "subscriber" && (
              <div className="mt-4 col-md-6 col-sm-12">
                <Doughnut
                  data={physicalResourcesData}
                  options={{
                    plugins: {
                      title: {
                        display: true,
                        text: "Physical Resource Reports",
                        position: "top",
                        font: { size: 16 },
                      },
                      legend: {
                        position: "right",
                        labels: { font: { size: 10 } },
                      },
                    },
                  }}
                />
              </div>
            )}
        </Charts>

        {isAreaCodeChartVisible &&
          (isAreaCodePending ? (
            <LoadingSpinner />
          ) : isAreaCodeError ? (
            <p>Failed to load Area Code Chart</p>
          ) : (
            <BarchartAreacode data={areaCodeData} />
          ))}

        {/* Worker hours chart code will start here */}
        {worker && worker.role === "super_admin" ? (
          <div>
            <StackbarcartWorkhours
              data={workHoursData?.original?.data}
              detailsData={workHoursDetailsData?.original?.data}
              isLoading={isWorkHoursPending}
              isDetailsLoading={isWorkHoursDetailsPending}
              isError={isWorkHoursError}
              isDetailsError={isWorkHoursDetailsError}
              onFilterChange={handleWorkHoursFilterChange}
              onBarClick={handleWorkHoursBarClick}
            />
          </div>
        ) : (
          ""
        )}
        {/* Worker hours chart code will end here */}
      </ChartsWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
const ExportOption = styled.select`
  height: 40px;
`;
const ToggleButtonWrapper = styled.div`
  width: 350px;
  font-size: 10pt;

  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }
`;

const ChartsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Charts = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  justify-content: space-around;
  div {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const DarkCard = styled.div`
  background-color: #fcd5c9;
  box-shadow: 10px 10px 5px whitesmoke;
  border-radius: 30px;
  text-align: center;
`;
const CardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DarkCardValue = styled.div`
  color: #ec3310;
  display: block;
  font-size: 5em;
  font-weight: 800;
`;

const DarkCardLabel = styled.div`
  vertical-align: bottom;
  color: #ff7234;
  text-transform: uppercase;
`;

const LightCard = styled.div`
  display: flex;
  flex-direction: column;

  background-color: #d9eef0;
  box-shadow: 10px 10px 5px whitesmoke;
  border-radius: 30px;
`;
const LightCardValue = styled.div`
  color: #178b96;
  display: block;
  font-size: 5em;
  font-weight: 800;
`;

const LightCardLabel = styled.div`
  vertical-align: bottom;
  color: #2db3c0;
  text-transform: uppercase;
`;
